/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { CustomerBreadcrumbs } from "~/components/Customer/CustomerBreadcrumbs";
import CustomerNavigation from "~/components/Customer/CustomerNavigation";
import { NavigationOnSide } from "~/components/NavigationOnSide/NavigationOnSide";
import ServiceCommitmentsNavigation from "~/components/Services/ServiceCommitmentsNavigation";
import { CustomerProvider, useCustomer } from "~/context/User/Customer";
import { CustomerCategory } from "~/models";
import { ROUTES } from "~/routing";
import { useTitle } from "~/tools";
import { PageNotFound } from "~/views/404";
import { AuditLogSearch } from "~/views/audit/AuditLogSearch";
import BulkUpdateResultView from "~/views/bulkUpdate/BulkUpdateResultView";
import BulkUpdateView from "~/views/bulkUpdate/BulkUpdateView";
import { ApplicationDetails } from "~/views/customer/applications/ApplicationDetails";
import { Applications } from "~/views/customer/applications/Applications";
import { EnvironmentDetails } from "~/views/customer/applications/EnvironmentDetails";
import { AssignmentRuleDetails } from "~/views/customer/assignmentRules/AssignmentRuleDetails/AssignmentRuleDetails";
import { AssignmentRules } from "~/views/customer/assignmentRules/AssignmentRulesPage/AssignmentRulesPage";
import { BasicInfo } from "~/views/customer/basicInfo/BasicInfo";
import { BillingRules } from "~/views/customer/billingRules/BillingRules";
import { Comments } from "~/views/customer/comments/Comments";
import { Contacts } from "~/views/customer/contacts/Contacts";
import { Contracts } from "~/views/customer/contracts/Contracts";
import { Products } from "~/views/customer/products/Products";
import { Runbook } from "~/views/customer/runbook/Runbook";
import CustomerServices from "~/views/customer/services/Services";
import { TeamMembers } from "~/views/customer/teamMembers/TeamMembers";
import { ToolsAndIntegrations } from "~/views/customer/toolsAndIntegrations/ToolsAndIntegrations";
import { Docs } from "~/views/Docs";
import { EmployeeDetails } from "~/views/employees/EmployeeDetails/EmployeeDetails";
import { EmployeesPage } from "~/views/employees/EmployeePage/EmployeesPage";
import { Maintenance } from "~/views/error/maintanance";
import { GlobalSettingsPage } from "~/views/globalSettings/globalSettingsPage";
import { HomePage } from "~/views/home/CustomersPage";
import { ProductCatalogPage } from "~/views/productCatalog/ProductCatalogPage";
import { ProductDetails } from "~/views/productCatalog/ProductDetails";
import { ReportPage } from "~/views/reporting/ReportPage";
import { ServiceAdd } from "~/views/serviceCommitments/ServiceAdd";
import { ServiceCatalogPage } from "~/views/serviceCommitments/ServiceCatalogPage";
import { ServiceCommitmentDetails } from "~/views/serviceCommitments/ServiceCommitmentDetails";
import { ServiceGroupsPage } from "~/views/serviceGroups/ServiceGroups";
import ServiceView from "~/views/services/details/ServiceView";
import ServiceOfferings from "~/views/services/ServiceOfferings";
import Services from "~/views/services/Services";
import ServicesNavigation from "~/views/services/ServicesNavigation";
import { ToolDetails } from "~/views/tooling/ToolDetails";
import { ToolPage } from "~/views/tooling/ToolPage";
import { ACTIONS } from "./actions";
import { GuardRoute } from "./GuardRoute";
import { ScrollToTop } from "./scrollToTop";
import { useAccess } from "./utils";

function MCModule() {
  const { customer } = useCustomer();
  const isOnlySaaS =
    customer?.info?.categories?.length === 1 &&
    customer?.info?.categories.includes(CustomerCategory.SAAS);
  const noSelectedCategory = customer?.info?.categories?.length === 0;

  return (
    <Switch>
      {(!isOnlySaaS || noSelectedCategory) && (
        <>
          <Route
            exact
            path={ROUTES.customers.applications}
            component={Applications}
          />
          <Route
            exact
            path={ROUTES.customers.assignmentRules}
            component={AssignmentRules}
          />
          <Route
            exact
            path={ROUTES.customers.billingRules}
            component={BillingRules}
          />
        </>
      )}
      <Route component={PageNotFound} />
    </Switch>
  );
}

function CustomerModule() {
  return (
    <Switch>
      <CustomerProvider>
        <NavigationOnSide
          breadcrumbs={<CustomerBreadcrumbs />}
          navigation={<CustomerNavigation />}
        >
          <Route exact path={ROUTES.customers.info} component={BasicInfo} />
          <Route
            exact
            path={ROUTES.customers.contracts}
            component={Contracts}
          />
          <Route
            exact
            path={ROUTES.customers.services}
            component={CustomerServices}
          />
          <Route exact path={ROUTES.customers.products} component={Products} />
          <Route exact path={ROUTES.customers.team} component={TeamMembers} />
          <Route
            exact
            path={[
              ROUTES.customers.applications,
              ROUTES.customers.assignmentRules,
              ROUTES.customers.billingRules,
            ]}
            component={MCModule}
          />

          <Route exact path={ROUTES.customers.contacts} component={Contacts} />
          <Route exact path={ROUTES.customers.comments} component={Comments} />
          <Route
            exact
            path={ROUTES.customers.tools}
            component={ToolsAndIntegrations}
          />
        </NavigationOnSide>
      </CustomerProvider>
    </Switch>
  );
}

function RunbookModule() {
  return (
    <Switch>
      <CustomerProvider>
        <Route exact path={ROUTES.customers.runbook} component={Runbook} />
      </CustomerProvider>
    </Switch>
  );
}

function AssignmentRuleModule() {
  return (
    <Switch>
      <CustomerProvider>
        <Route
          exact
          path={ROUTES.customers.assignmentRuleDetails}
          component={AssignmentRuleDetails}
        />
      </CustomerProvider>
    </Switch>
  );
}

function ApplicationModule() {
  return (
    <Switch>
      <CustomerProvider>
        <Route
          exact
          path={ROUTES.customers.applicationDetails}
          component={ApplicationDetails}
        />
      </CustomerProvider>
    </Switch>
  );
}

function EnvironmentModule() {
  return (
    <Switch>
      <CustomerProvider>
        <Route
          exact
          path={ROUTES.customers.environment}
          component={EnvironmentDetails}
        />
      </CustomerProvider>
    </Switch>
  );
}

function GlobalSwitch() {
  const { checkAccess } = useAccess();

  useTitle();

  return (
    <>
      <ScrollToTop />
      <Switch>
        {/* customers */}
        <Route exact path={ROUTES.index}>
          <Redirect to={ROUTES.customers.index} />
        </Route>
        <Route
          exact
          path={ROUTES.customers.details}
          render={({ match }) => (
            <Redirect to={`/customers/${match?.params?.customerId}/info/`} />
          )}
        />
        {/* <CustomerRoutes /> */}
        <Route
          exact
          path={[
            ROUTES.customers.info,
            ROUTES.customers.services,
            ROUTES.customers.products,
            ROUTES.customers.contracts,
            ROUTES.customers.team,
            ROUTES.customers.applications,
            ROUTES.customers.assignmentRules,
            ROUTES.customers.billingRules,
            ROUTES.customers.contacts,
            ROUTES.customers.comments,
            ROUTES.customers.tools,
          ]}
          component={CustomerModule}
        />

        <Route exact path={ROUTES.customers.index} component={HomePage} />

        {/* employees */}
        <Route exact path={ROUTES.employees.index} component={EmployeesPage} />
        <Route
          exact
          path={ROUTES.employees.details}
          component={EmployeeDetails}
        />

        {/* services */}
        <Route exact path={ROUTES.services.details} component={ServiceView} />
        <Route path={[ROUTES.services.index, ROUTES.services.offerings]}>
          <NavigationOnSide navigation={<ServicesNavigation />}>
            <Route exact path={ROUTES.services.index} component={Services} />
            <Route
              exact
              path={ROUTES.services.offerings}
              component={ServiceOfferings}
            />
          </NavigationOnSide>
        </Route>

        <Route
          exact
          path={ROUTES.serviceCommitments.add}
          component={ServiceAdd}
        />
        <Route
          exact
          path={ROUTES.serviceCommitments.details}
          component={ServiceCommitmentDetails}
        />

        {/* service groups */}
        <Route
          path={[ROUTES.serviceCommitments.index, ROUTES.serviceGroups.index]}
        >
          <NavigationOnSide navigation={<ServiceCommitmentsNavigation />}>
            <Route
              exact
              path={ROUTES.serviceGroups.index}
              component={ServiceGroupsPage}
            />
            <Route
              exact
              path={ROUTES.serviceCommitments.index}
              component={ServiceCatalogPage}
            />
          </NavigationOnSide>
        </Route>

        {/* tooling */}
        <Route exact path={ROUTES.tool.index} component={ToolPage} />
        <Route exact path={ROUTES.tool.details} component={ToolDetails} />

        {/* reporting */}
        <Route exact path={ROUTES.reporting.index} component={ReportPage} />

        {/* docs: swagger */}
        <Route exact path={ROUTES.docs} component={Docs} />

        {/* product */}
        <Route
          exact
          path={ROUTES.productCatalog.index}
          component={ProductCatalogPage}
        />
        <Route
          exact
          path={ROUTES.productCatalog.details}
          component={ProductDetails}
        />

        <GuardRoute
          grantAccess={checkAccess(ACTIONS.VISIT_GLOBAL_SETTINGS)}
          exact
          path={ROUTES.globalSettings}
          component={GlobalSettingsPage}
        />
        <Route
          exact
          path={ROUTES.customers.assignmentRuleDetails}
          component={AssignmentRuleModule}
        />
        <Route
          exact
          path={ROUTES.customers.applicationDetails}
          component={ApplicationModule}
        />
        <Route
          exact
          path={ROUTES.customers.environment}
          component={EnvironmentModule}
        />
        <Route
          exact
          path={ROUTES.customers.runbook}
          component={RunbookModule}
        />
        <Route exact path={ROUTES.bulkUpdate} component={BulkUpdateView} />
        <Route
          exact
          path={ROUTES.bulkUpdateResult}
          component={BulkUpdateResultView}
        />
        <Route exact path={ROUTES.maintenance} component={Maintenance} />
        <Route exact path={ROUTES.audit} component={AuditLogSearch} />
        <Route component={PageNotFound} />
      </Switch>
    </>
  );
}

export default GlobalSwitch;
